import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import {
  doc, collection, getDoc, getDocs, updateDoc, addDoc, deleteDoc,
  query, orderBy, serverTimestamp, writeBatch
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import ProjectTable from '../components/ProjectTable';

function ProjectView() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [stages, setStages] = useState([]);
  const [newStageName, setNewStageName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [editingClient, setEditingClient] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newClient, setNewClient] = useState('');
  const [editingStageId, setEditingStageId] = useState(null);
  const [newEditingStageName, setNewEditingStageName] = useState('');
  const [userData, setUserData] = useState(null);

  const inputNameRef = useRef(null);
  const inputClientRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          setUserData(userDoc.data());

          const projectRef = doc(db, 'projects', projectId);
          const projectSnap = await getDoc(projectRef);
          if (projectSnap.exists()) {
            const data = projectSnap.data();
            setProject({ id: projectSnap.id, ...data });
            setNewProjectName(data.name || '');
            setNewClient(data.client || '');
          }

          const stagesCol = collection(db, 'projects', projectId, 'stages');
          const stagesQuery = query(stagesCol, orderBy('order'));
          const stagesSnap = await getDocs(stagesQuery);
          const stagesList = stagesSnap.docs.map((doc, index) => ({
            id: doc.id,
            ...doc.data(),
            order: doc.data().order ?? index
          }));
          setStages(stagesList);
        } catch (error) {
          console.error("Erro ao carregar dados:", error);
        }
      } else {
        setUserData(null);
      }
    });

    return () => unsubscribe();
  }, [projectId]);

  const isAdmin = userData?.role === 'admin';

  const totalProgress = useMemo(() => {
    let total = 0;
    let completed = 0;
    stages.forEach(stage => {
      stage.activities?.forEach(activity => {
        total += 1;
        if (activity.status === 'Concluído') completed += 1;
      });
    });
    return total === 0 ? 0 : Math.round((completed / total) * 100);
  }, [stages]);

  const saveProjectName = async () => {
    const trimmed = newProjectName.trim();
    if (!trimmed || trimmed === project.name) {
      setEditingName(false);
      return;
    }
    try {
      await updateDoc(doc(db, 'projects', projectId), { name: trimmed });
      setProject(prev => ({ ...prev, name: trimmed }));
      setEditingName(false);
    } catch {
      alert('Erro ao salvar nome do projeto');
    }
  };

  const saveProjectClient = async () => {
    const trimmed = newClient.trim();
    if (trimmed === project.client) {
      setEditingClient(false);
      return;
    }
    try {
      await updateDoc(doc(db, 'projects', projectId), { client: trimmed });
      setProject(prev => ({ ...prev, client: trimmed }));
      setEditingClient(false);
    } catch {
      alert('Erro ao salvar cliente');
    }
  };

  const saveStageName = async (stageId) => {
    const trimmed = newEditingStageName.trim();
    if (!trimmed) return;
    try {
      await updateDoc(doc(db, 'projects', projectId, 'stages', stageId), { name: trimmed });
      setStages(prev => prev.map(stage =>
        stage.id === stageId ? { ...stage, name: trimmed } : stage
      ));
      setEditingStageId(null);
      setNewEditingStageName('');
    } catch {
      alert('Erro ao salvar nome da etapa');
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (!window.confirm('Deseja realmente excluir esta etapa?')) return;
    try {
      await deleteDoc(doc(db, 'projects', projectId, 'stages', stageId));
      setStages(prev => prev.filter(stage => stage.id !== stageId));
    } catch {
      alert('Erro ao excluir etapa');
    }
  };

  const handleAddStage = async () => {
    const trimmed = newStageName.trim();
    if (!trimmed) return;
    try {
      const order = stages.length;
      const stageDoc = await addDoc(collection(db, 'projects', projectId, 'stages'), {
        name: trimmed,
        activities: [],
        createdAt: serverTimestamp(),
        order
      });
      setStages(prev => [
        ...prev,
        { id: stageDoc.id, name: trimmed, activities: [], createdAt: new Date(), order }
      ]);
      setNewStageName('');
    } catch {
      alert('Erro ao adicionar etapa');
    }
  };

  const handleEditChange = (updatedActivity) => {
    setStages(prev => prev.map(stage => {
      if (stage.id !== updatedActivity.stageId) return stage;
      const newActivities = stage.activities.map((act, idx) =>
        idx === updatedActivity.activityIndex ? updatedActivity : act
      );
      return { ...stage, activities: newActivities };
    }));
  };

  const handleEditSave = async (updatedActivity) => {
    const stageRef = doc(db, 'projects', projectId, 'stages', updatedActivity.stageId);
    try {
      const stage = stages.find(s => s.id === updatedActivity.stageId);
      await updateDoc(stageRef, { activities: stage.activities });
    } catch {
      alert('Erro ao salvar atividade');
    }
  };

  const handleDeleteSelected = async (selected) => {
    const stageMap = {};
    selected.forEach(act => {
      if (!stageMap[act.stageId]) stageMap[act.stageId] = [];
      stageMap[act.stageId].push(act.activityIndex);
    });

    try {
      for (const stageId in stageMap) {
        const stage = stages.find(s => s.id === stageId);
        const newActivities = stage.activities.filter((_, idx) =>
          !stageMap[stageId].includes(idx)
        );
        await updateDoc(doc(db, 'projects', projectId, 'stages', stageId), { activities: newActivities });
        setStages(prev => prev.map(s =>
          s.id === stageId ? { ...s, activities: newActivities } : s
        ));
      }
    } catch {
      alert('Erro ao deletar atividades');
    }
  };

  const handleDuplicateSelected = async (selected) => {
    const stageMap = {};
    selected.forEach(act => {
      if (!stageMap[act.stageId]) stageMap[act.stageId] = [];
      stageMap[act.stageId].push(act);
    });

    try {
      for (const stageId in stageMap) {
        const stage = stages.find(s => s.id === stageId);
        const newActivities = [
          ...stage.activities,
          ...stageMap[stageId].map(act => {
            const { activityIndex, stageId, ...rest } = act;
            return { ...rest };
          })
        ];
        await updateDoc(doc(db, 'projects', projectId, 'stages', stageId), { activities: newActivities });
        setStages(prev => prev.map(s =>
          s.id === stageId ? { ...s, activities: newActivities } : s
        ));
      }
    } catch {
      alert('Erro ao duplicar atividades');
    }
  };

  const handleAddActivity = async (newActivity) => {
    if (!newActivity || !newActivity.stageId) return;

    try {
      const stage = stages.find(s => s.id === newActivity.stageId);
      const updatedActivities = [...(stage.activities || []), newActivity];
      await updateDoc(doc(db, 'projects', projectId, 'stages', newActivity.stageId), { activities: updatedActivities });
      setStages(prev => prev.map(s =>
        s.id === newActivity.stageId ? { ...s, activities: updatedActivities } : s
      ));
    } catch {
      alert('Erro ao adicionar atividade');
    }
  };

  const handleSignStage = async (stageId) => {
    if (!window.confirm('Confirma o aceite desta etapa?')) return;

    const signedAt = new Date().toISOString();

    try {
      await updateDoc(doc(db, 'projects', projectId, 'stages', stageId), {
        signed: true,
        signedAt
      });

      setStages(prev => prev.map(stage =>
        stage.id === stageId ? { ...stage, signed: true, signedAt } : stage
      ));
    } catch {
      alert('Erro ao assinar a etapa');
    }
  };

  const handleStageDrag = async (dragIndex, hoverIndex) => {
    const reordered = [...stages];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(hoverIndex, 0, moved);
    setStages(reordered);

    const batch = writeBatch(db);
    reordered.forEach((stage, index) => {
      const ref = doc(db, 'projects', projectId, 'stages', stage.id);
      batch.update(ref, { order: index });
    });
    await batch.commit();
  };

  const handleActivityDrag = async (stageId, fromIndex, toIndex) => {
    const stage = stages.find(s => s.id === stageId);
    if (!stage) return;
    const updated = [...stage.activities];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);

    await updateDoc(doc(db, 'projects', projectId, 'stages', stageId), {
      activities: updated
    });

    setStages(prev => prev.map(s =>
      s.id === stageId ? { ...s, activities: updated } : s
    ));
  };

  if (!project) return <p className="text-center mt-20">Carregando...</p>;

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-6">
      <div className="flex justify-end space-x-2">
        <button onClick={() => navigate(`/projects/${projectId}/files`)} className="bg-[#b29d92] text-white px-3 py-1 rounded font-semibold">Arquivos</button>
        <button onClick={() => navigate(`/projects/${projectId}/finance`)} className="bg-[#b29d92] text-white px-3 py-1 rounded font-semibold">Financeiro</button>
        <button onClick={() => navigate(`/projects/${projectId}/gantt`)} className="bg-[#b29d92] text-white px-3 py-1 rounded font-semibold">Gantt</button>
      </div>

      <div className="flex justify-between items-center">
        {isAdmin && editingName ? (
          <input
            ref={inputNameRef}
            value={newProjectName}
            onChange={e => setNewProjectName(e.target.value)}
            onBlur={saveProjectName}
            className="border px-3 py-2 rounded w-full max-w-md text-2xl font-bold"
          />
        ) : (
          <h1
            className={`text-2xl font-bold text-gray-800 ${isAdmin ? 'cursor-pointer' : ''}`}
            onClick={() => isAdmin && setEditingName(true)}
          >
            {project.name}
          </h1>
        )}
        <div className="text-gray-600 font-semibold text-lg min-w-[120px] text-right">
          Progresso total: {totalProgress}%
        </div>
      </div>

      <div>
        {isAdmin && editingClient ? (
          <input
            ref={inputClientRef}
            value={newClient}
            onChange={e => setNewClient(e.target.value)}
            onBlur={saveProjectClient}
            className="border px-3 py-2 rounded w-full max-w-md text-lg text-gray-700"
          />
        ) : (
          <p
            className={`${isAdmin ? 'cursor-pointer' : ''} max-w-md text-lg text-gray-600`}
            onClick={() => isAdmin && setEditingClient(true)}
          >
            Cliente:{' '}
            {project.client || (
              <span className="italic text-gray-400">Nenhum cliente definido</span>
            )}
          </p>
        )}
      </div>

      {stages.map((stage, index) => (
        <div
          key={stage.id}
          className="space-y-2 border-b pb-4 bg-white"
        >
          <div className="flex justify-start items-center space-x-2">
            {isAdmin && editingStageId === stage.id ? (
              <input
                value={newEditingStageName}
                onChange={e => setNewEditingStageName(e.target.value)}
                onBlur={() => saveStageName(stage.id)}
                className="border px-2 py-1 rounded text-xl font-semibold max-w-[80%]"
                autoFocus
              />
            ) : (
              <h2
                className={`text-xl font-semibold ${
                  isAdmin ? 'cursor-move' : ''
                } max-w-[80%] truncate`}
                draggable={isAdmin}
                onDragStart={e => e.dataTransfer.setData('text/plain', index)}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  const from = parseInt(
                    e.dataTransfer.getData('text/plain'),
                    10
                  );
                  if (from !== index) handleStageDrag(from, index);
                }}
                onClick={() => {
                  if (isAdmin) {
                    setEditingStageId(stage.id);
                    setNewEditingStageName(stage.name);
                  }
                }}
              >
                {stage.name}
              </h2>
            )}
            {isAdmin && (
              <button
                onClick={() => handleDeleteStage(stage.id)}
                className="text-red-500 hover:text-red-700 ml-2"
                title="Excluir etapa"
              >
                🗑️
              </button>
            )}
          </div>

          {stage.signed && (
            <p className="text-green-600 text-sm">
              ✅ Etapa concluída com aceite do cliente em:{' '}
              {new Date(stage.signedAt).toLocaleString()}
            </p>
          )}

          <ProjectTable
            activities={
              stage.activities?.map((activity, idx) => ({
                ...activity,
                stageId: stage.id,
                activityIndex: idx
              })) || []
            }
            onEditInline={isAdmin ? handleEditChange : null}
            onEditSave={isAdmin ? handleEditSave : null}
            onEditChange={isAdmin ? handleEditChange : null}
            onDeleteSelected={isAdmin ? handleDeleteSelected : null}
            onDuplicateSelected={isAdmin ? handleDuplicateSelected : null}
            onAddActivity={
              isAdmin
                ? newActivity => handleAddActivity({ ...newActivity, stageId: stage.id })
                : null
            }
            onActivityDrag={isAdmin ? handleActivityDrag : null}
          />

          {!isAdmin && !stage.signed && (
            <div className="flex justify-end mt-2">
              <button
                onClick={() => handleSignStage(stage.id)}
                className="bg-[#7b5e57] hover:bg-[#5e453f] text-white px-3 py-1 rounded"
              >
                Assinar Conclusão
              </button>
            </div>
          )}
        </div>
      ))}

      {isAdmin && (
        <div className="space-x-2">
          <input
            type="text"
            value={newStageName}
            onChange={e => setNewStageName(e.target.value)}
            placeholder="Nome da nova etapa"
            className="border px-3 py-2 rounded"
          />
          <button
            onClick={handleAddStage}
            className="bg-[#b29d92] hover:bg-[#b29d92] text-white px-3 py-1 rounded"
          >
            Adicionar Etapa
          </button>
        </div>
      )}
    </div>
  );
}

export default ProjectView;
